/**
 * ViewportProbe.tsx
 *
 * A diagnostic readout for the one question three rounds of keyboard fixes never
 * answered, behind `?vvdebug=1`.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────────
 * SCRUM-1363/1364/1366 all report the same thing: the keyboard opens and the whole
 * surface is pushed up. The sizing was never the defect — `--vv-height` is the visible
 * band, so the composer was already above the keyboard. What displaces the surface is
 * the browser's own scroll-to-reveal at focus time, and that arrives as one of exactly
 * two mechanisms:
 *
 *   window.scrollY > 0        the DOCUMENT scrolled. Recoverable — a scroll lock holds
 *                             it, and `window.scrollTo` puts it back.
 *   visualViewport.offsetTop  the VISUAL VIEWPORT is offset inside a layout viewport
 *     > 0                     that did not move. Not settable from script at all; only
 *                             a `fixed` surface can compensate, and only if it knows
 *                             which way `fixed` is anchored.
 *
 * They need opposite fixes. `ui/docs/mobile-device-pass-1363-1364.md` specified a probe
 * for this, but it has to be pasted into Safari's Web Inspector — which needs a Mac, so
 * it was never run and every pass column in the migration log still reads "not run".
 * This is the same measurement with no tooling requirement: open the flag, tap the
 * composer, screenshot.
 *
 * ── Why it writes to the DOM instead of setState ─────────────────────────────────
 * Same reason `useVisualViewportVars` publishes custom properties: these events fire
 * many times a second through the keyboard animation, and a probe that re-renders the
 * tree it is measuring is measuring itself.
 *
 * `own` is the probe's own `getBoundingClientRect().top`. It is a control: this panel is
 * `fixed` with `top: 0`, so if `own` is anything but 0 while `offsetTop` is non-zero,
 * that is the anchoring answer directly — `fixed` is following the visual viewport, and
 * every `top: var(--vv-offset-top)` in the app is double-counting.
 */
import { useEffect, useRef } from 'react';

/**
 * Read once at module load, like the desktop kill switch in `useBreakpoint.ts`. A
 * live-reactive flag would mount a measurement surface mid-interaction, which is the
 * one moment it must not change anything.
 */
const ENABLED = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('vvdebug');
  } catch {
    // A malformed search string must never take a page down for a debug flag.
    return false;
  }
})();

export function ViewportProbe() {
  const ref = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!ENABLED) return;

    const node = ref.current;
    if (!node) return;

    const vv = window.visualViewport;
    const root = document.documentElement;
    let frame = 0;
    let peakScrollY = 0;
    let peakOffsetTop = 0;
    let peakScrollX = 0;
    let focusedAt = 0;
    // What the screen looked like at the moment of the largest displacement. The peaks
    // alone (SCRUM-1363's 2026-10-07 reading: 460 / 459, both back to 0 by the
    // screenshot) say THAT the surface moved but not which way `fixed` was anchored
    // while it did — `own rect.top` read at rest is 0 either way. Captured on each new
    // peak so the screenshot carries the frame that decides the fix.
    let atPeak = 'none yet';

    const render = () => {
      frame = 0;

      const scrollY = Math.round(window.scrollY);
      const scrollX = Math.round(window.scrollX);
      const offsetTop = vv ? Math.round(vv.offsetTop) : -1;
      const own = Math.round(node.getBoundingClientRect().top);

      // Peaks, not just current values. The displacement these tickets describe can be
      // transient — applied at focus and corrected a frame later — and a reading taken
      // after the keyboard settles would show 0 for the very thing that moved.
      // `scrollX` counts too: the width report is a sideways shift, and it can happen
      // with no vertical movement at all.
      const newPeak = scrollY > peakScrollY || offsetTop > peakOffsetTop || scrollX > peakScrollX;
      peakScrollY = Math.max(peakScrollY, scrollY);
      peakOffsetTop = Math.max(peakOffsetTop, offsetTop);
      peakScrollX = Math.max(peakScrollX, scrollX);

      if (newPeak) {
        const since = focusedAt ? `${Math.round(performance.now() - focusedAt)}ms` : 'no focus';
        atPeak = `y${scrollY} x${scrollX} vvTop${offsetTop} own${own} iw${window.innerWidth} +${since}`;
      }

      const styles = getComputedStyle(root);
      // The focused control's horizontal extent. The same reading showed the composer
      // running to the right edge with Send off-screen and the left padding intact; a
      // right edge past innerWidth is that, measured.
      const focused = document.activeElement;
      const focusRect =
        focused && focused !== document.body
          ? (({ left, right }) => `${Math.round(left)}..${Math.round(right)}`)(focused.getBoundingClientRect())
          : '-';

      node.textContent = [
        `scrollY      ${scrollY}   peak ${peakScrollY}`,
        `vv.offsetTop ${offsetTop}   peak ${peakOffsetTop}`,
        `at peak      ${atPeak}`,
        `scrollX      ${scrollX}   peak ${peakScrollX}`,
        `innerWidth   ${window.innerWidth}  vv.width ${vv ? Math.round(vv.width) : -1}`,
        `doc scrollW  ${root.scrollWidth}  clientW ${root.clientWidth}`,
        `focus x      ${focusRect}`,
        `vv.height    ${vv ? Math.round(vv.height) : -1}`,
        `innerHeight  ${window.innerHeight}`,
        `vv.scale     ${vv ? vv.scale.toFixed(2) : '-'}`,
        `own rect.top ${own}`,
        `--vv-height         ${styles.getPropertyValue('--vv-height').trim() || 'unset'}`,
        `--vv-offset-top     ${styles.getPropertyValue('--vv-offset-top').trim() || 'unset'}`,
        `--vv-keyboard-inset ${styles.getPropertyValue('--vv-keyboard-inset').trim() || 'unset'}`,
      ].join('\n');
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(render);
    };

    render();

    // Separate from `schedule` because the timestamp must be the event's, not the next
    // frame's: "the peak landed N ms after focus" is how a focus-time scroll-to-reveal is
    // told apart from one applied later by the keyboard animation.
    const markFocus = () => {
      focusedAt = performance.now();
    };
    document.addEventListener('focusin', markFocus, true);

    // `scroll` on window catches the document-scroll mechanism; the visualViewport
    // listeners catch the offset one. Both are needed because either one alone reads as
    // "nothing moved" for the other's failure mode.
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('focusin', schedule, true);
    document.addEventListener('focusout', schedule, true);
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('focusin', markFocus, true);
      document.removeEventListener('focusin', schedule, true);
      document.removeEventListener('focusout', schedule, true);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
    };
  }, []);

  if (!ENABLED) return null;

  return (
    <pre
      ref={ref}
      // `top-0 left-0` and not centred: the numbers have to stay readable in a
      // screenshot taken while the layout is displaced, and a corner is the only place
      // that survives that. Above the AI overlay's z-40 so it is visible on the surface
      // being measured, below the cookie banner's z-[9999].
      className="fixed left-0 top-0 z-[9998] m-0 whitespace-pre bg-black/80 p-1.5 font-mono text-[10px] leading-tight text-lime-300"
    />
  );
}
